import { writable } from "svelte/store";

// What content and widgets share: one map of stores by name, filled by keep
// and handed out by fetch; the names a tree of definitions draws from; and
// the server's answer read as JSON, a failed call reported once.

/**
 * Reads the server's answer as JSON; a failed call is reported here and
 * answers nothing.
 * @param {Promise<Response>} call - the fetch made
 * @returns {Promise<any>}
 */
export function answered(call) {
  return call
    .then((resp) => {
      return resp.json();
    })
    .catch((err) => {
      console.debug(err);
    });
}

/**
 * Adds the name def draws from, and each its parts draw from, to list
 * @param {any} def - definition of a component
 * @param {(def: any) => unknown} keyOf - the name a definition draws from, or nothing
 * @param {string[]} list - list to populate
 */
function search(def, keyOf, list) {
  if (!def || typeof def !== "object") {
    return;
  }
  if (Array.isArray(def.c)) {
    def.c.forEach(function (/** @type {any} */ child) {
      search(child, keyOf, list);
    });
  }

  const name = keyOf(def);
  if (typeof name === "string" && name !== "") {
    list.push(name);
  }
}

/**
 * A loader's stores: one per name, made empty by fetch and filled by keep.
 * @template T
 * @param {(def: any) => unknown} keyOf - the name a definition draws from, or nothing
 * @param {() => T} empty - what a store holds until its name is loaded
 */
export function stores(keyOf, empty) {
  /** @type {Record<string, import("svelte/store").Writable<T>>} */
  const kept = {};

  return {
    /**
     * Fetches the store for a name, made empty if none is kept yet
     * @param {string} name
     * @returns {Promise<import("svelte/store").Writable<T>>}
     */
    fetch: async (name) => {
      kept[name] ??= writable(empty());
      return kept[name];
    },
    /**
     * Keeps what the server answered for a name, telling the parts drawing it
     * @param {string} name
     * @param {T} value
     */
    keep: (name, value) => {
      if (kept[name]) {
        kept[name].set(value);
      } else {
        kept[name] = writable(value);
      }
    },
    /**
     * Finds the names within any definitions
     * @param {unknown} defs - definitions to search
     * @returns {string[]}
     */
    names: (defs) => {
      /** @type {string[]} */
      const list = [];
      if (Array.isArray(defs)) {
        defs.forEach(function (def) {
          search(def, keyOf, list);
        });
      }

      return list;
    },
  };
}
